/**
 * sign-in-errors.ts — turn a failed customer sign-in into the sentence the customer reads.
 *
 * ⭐ WHY THIS EXISTS (2 October 2026). Customer sign-in now carries a bot check, enforced by the
 * subscriber pool's PreAuthentication trigger (`nexa-subscriber-pre-auth`). A trigger refusal does
 * not arrive as a Cognito error name the pages already handle — it arrives as
 * `UserLambdaValidationException` with the message `PreAuthentication failed with error <msg>.`,
 * and the pages used to print `err.message` verbatim for anything they did not recognise. The
 * trigger's own message is written for the customer, so show exactly that, and nothing else.
 * The same wrapper carried the 60-minute support-password expiry message (ADR-061), which was
 * shown with its Cognito prefix until now.
 *
 * Shared by every sign-in entry point (SignInPage, SignInEmbedPage, WelcomePage) so the three
 * cannot drift apart.
 */

/** Matches the PreAuthentication wrapper and captures the trigger's own message. */
const PRE_AUTH_REFUSAL = /PreAuthentication failed with error (.+?)\.?$/;

/** Is this error a refusal by the pre-authentication trigger (bot check, expired temp password)? */
export function isPreAuthRefusal(err: unknown): boolean {
  const msg = (err as { message?: string } | null)?.message ?? '';
  return PRE_AUTH_REFUSAL.test(msg);
}

/**
 * Amplify's refusal to sign in while a session already exists ("There is already a signed in
 * user."). `signInFresh` clears every zone and retries once before this can reach a page, so
 * seeing it means something kept writing a session back — another tab mid-sign-in, typically.
 */
export function isExistingSessionError(err: unknown): boolean {
  return (err as { name?: string } | null)?.name === 'UserAlreadyAuthenticatedException';
}

/** What a person reads when that refusal survives the retry (Q13, 31 October 2026). */
export const EXISTING_SESSION_MESSAGE =
  'Another Nexanomics tab is still signing someone in. Wait a moment, then sign in again.';

/** The customer-facing sentence for a failed sign-in. Never a raw Cognito name or wrapper. */
export function signInErrorMessage(err: unknown, fallback = 'Sign-in failed. Please try again.'): string {
  const e = (err ?? {}) as { name?: string; message?: string };
  if (e.name === 'NotAuthorizedException' || e.name === 'UserNotFoundException') {
    return 'Incorrect email or password.';
  }
  // ⛔ Never Amplify's own "There is already a signed in user." — it names no action a person can take.
  if (isExistingSessionError(err)) return EXISTING_SESSION_MESSAGE;
  const preAuth = PRE_AUTH_REFUSAL.exec(e.message ?? '');
  if (preAuth?.[1]) return preAuth[1].replace(/\.+$/, '.');
  return e.message || fallback;
}
